import api from './api';
import { hasSessionHint } from './authSession';

export interface Notification {
    id: string;
    title: string;
    message: string;
    type: string;
    priority: string;
    icon?: string;
    action_url?: string;
    image_url?: string;
    data?: Record<string, any>;
    is_read: boolean;
    is_sent: boolean;
    created_at: string;
    read_at?: string;
    time_ago: string;
}

export interface NotificationStats {
    total_count: number;
    unread_count: number;
    today_count: number;
    by_type: Record<string, number>;
    by_priority: Record<string, number>;
}

export interface NotificationPreferences {
    achievements_enabled: boolean;
    lesson_reminders_enabled: boolean;
    streak_reminders_enabled: boolean;
    social_notifications_enabled: boolean;
    system_notifications_enabled: boolean;
    web_push_enabled: boolean;
    fcm_enabled: boolean;
    email_enabled: boolean;
    quiet_hours_start?: string;
    quiet_hours_end?: string;
    timezone: string;
    max_daily_notifications: number;
}

export interface NotificationConfig {
    web_push_enabled: boolean;
    fcm_enabled: boolean;
    vapid_public_key?: string;
    supported_features: {
        web_push: boolean;
        fcm: boolean;
        templates: boolean;
        preferences: boolean;
        statistics: boolean;
    };
}

export interface PaginatedNotifications {
    count: number;
    next?: string;
    previous?: string;
    results: Notification[];
}

interface NotificationData {
    title: string;
    message: string;
    type: 'pronunciation_alert' | 'achievement' | 'reminder' | 'practice_report';
    priority: 'low' | 'normal' | 'high';
    icon?: string;
    data?: any;
}

interface NotificationOptions {
    body: string;
    icon?: string;
    badge?: string;
    tag?: string;
    data?: any;
    requireInteraction?: boolean;
    silent?: boolean;
}

class NotificationService {
    private vapidPublicKey?: string;
    private fcmSupported = false;
    private webPushSupported = false;
    private registration?: ServiceWorkerRegistration;
    private permission: NotificationPermission = 'default';
    private isSupported: boolean;

    constructor() {
        this.isSupported = 'Notification' in window;
        this.permission = this.isSupported ? Notification.permission : 'denied';
        this.initializeNotificationSystem();
        this.checkPermission();
        this.registerServiceWorker();
    }

    private async initializeNotificationSystem() {
        try {
            if (!this.isSupported) {
                return;
            }
            if (!hasSessionHint()) {
                return;
            }
            // Get notification configuration from backend
            const config = await this.getConfig();
            this.vapidPublicKey = config.vapid_public_key || '';
            this.webPushSupported = config.web_push_enabled && 'serviceWorker' in navigator && 'PushManager' in window;
            this.fcmSupported = config.fcm_enabled;

            // Register service worker for Web Push
            if (this.webPushSupported) {
                await this.registerServiceWorker();
            }

            // Initialize FCM if supported
            if (this.fcmSupported) {
                await this.initializeFCM();
            }
        } catch (error) {
            console.error('Failed to initialize notification system:', error);
        }
    }

    private async registerServiceWorker(): Promise<void> {
        try {
            this.registration = await navigator.serviceWorker.register('/sw.js');
            console.log('Service Worker registered successfully');
        } catch (error) {
            console.error('Service Worker registration failed:', error);
        }
    }

    private async initializeFCM(): Promise<void> {
        try {
            // Dynamically import Firebase only if FCM is supported
            const { initializeApp } = await import('firebase/app');
            const { getMessaging, onMessage } = await import('firebase/messaging');

            // Initialize Firebase app (config should be provided by environment)
            const firebaseConfig = {
                apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
                authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
                projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
                storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
                messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
                appId: import.meta.env.VITE_FIREBASE_APP_ID,
            };

            const app = initializeApp(firebaseConfig);
            const messaging = getMessaging(app);

            // Handle foreground messages
            onMessage(messaging, (payload) => {
                console.log('Received foreground message:', payload);
                this.handleForegroundMessage(payload);
            });

            console.log('FCM initialized successfully');
        } catch (error) {
            console.error('FCM initialization failed:', error);
            this.fcmSupported = false;
        }
    }

    private handleForegroundMessage(payload: any): void {
        // Show notification manually when app is in foreground
        if (payload.notification) {
            this.showLocalNotification(
                payload.notification.title,
                payload.notification.body,
                payload.notification.icon,
                payload.data,
            );
        }
    }

    private showLocalNotification(title: string, body: string, icon?: string, data?: any): void {
        if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(title, {
                body,
                icon: icon || '/icons/notification-icon.png',
                badge: '/icons/badge-icon.png',
                data,
                tag: data?.id || 'notification',
            });
        }
    }

    // API Methods
    async getConfig(): Promise<NotificationConfig> {
        try {
            const response = await api.get('/notifications/config/');
            return response.data;
        } catch (error) {
            console.error('Failed to get notification config:', error);
            return {
                web_push_enabled: false,
                fcm_enabled: false,
                supported_features: {
                    web_push: false,
                    fcm: false,
                    templates: false,
                    preferences: false,
                    statistics: false,
                },
            };
        }
    }

    async getNotifications(params?: {
        page?: number;
        page_size?: number;
        is_read?: boolean;
        type?: string;
        priority?: string;
        from_date?: string;
        to_date?: string;
    }): Promise<PaginatedNotifications> {
        try {
            const response = await api.get('/notifications/', { params });
            return response.data;
        } catch (error) {
            console.error('Failed to get notifications:', error);
            return {
                count: 0,
                results: [],
            };
        }
    }

    async getNotification(id: string): Promise<Notification> {
        try {
            const response = await api.get(`/notifications/${id}/`);
            return response.data;
        } catch (error) {
            console.error('Failed to get notification:', error);
            throw error;
        }
    }

    async markNotificationsAsRead(notificationIds?: string[]): Promise<{ message: string; updated_count: number }> {
        try {
            const response = await api.post('/notifications/mark-read/', {
                notification_ids: notificationIds,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to mark notifications as read:', error);
            return { message: 'Failed to mark notifications as read', updated_count: 0 };
        }
    }

    async deleteNotification(id: string): Promise<void> {
        try {
            await api.delete(`/notifications/${id}/delete/`);
        } catch (error) {
            console.error('Failed to delete notification:', error);
        }
    }

    async deleteAllNotifications(): Promise<{ message: string; deleted_count: number }> {
        try {
            const response = await api.delete('/notifications/delete-all/');
            return response.data;
        } catch (error) {
            console.error('Failed to delete all notifications:', error);
            return { message: 'Failed to delete notifications', deleted_count: 0 };
        }
    }

    async getStats(): Promise<NotificationStats> {
        try {
            const response = await api.get('/notifications/stats/');
            return response.data;
        } catch (error) {
            console.error('Failed to get notification stats:', error);
            return {
                total_count: 0,
                unread_count: 0,
                today_count: 0,
                by_type: {},
                by_priority: {},
            };
        }
    }

    async getPreferences(): Promise<NotificationPreferences> {
        try {
            const response = await api.get('/notifications/preferences/');
            return response.data;
        } catch (error) {
            console.error('Failed to get notification preferences:', error);
            return {
                achievements_enabled: true,
                lesson_reminders_enabled: true,
                streak_reminders_enabled: true,
                social_notifications_enabled: true,
                system_notifications_enabled: true,
                web_push_enabled: false,
                fcm_enabled: false,
                email_enabled: true,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                max_daily_notifications: 10,
            };
        }
    }

    async updatePreferences(preferences: Partial<NotificationPreferences>): Promise<NotificationPreferences> {
        try {
            const response = await api.patch('/notifications/preferences/', preferences);
            return response.data;
        } catch (error) {
            console.error('Failed to update notification preferences:', error);
            throw error;
        }
    }

    async sendTestNotification(data?: {
        title?: string;
        message?: string;
        type?: string;
        priority?: string;
    }): Promise<{ message: string; notification_id: string }> {
        try {
            const response = await api.post('/notifications/test/', data);
            return response.data;
        } catch (error) {
            console.error('Failed to send test notification:', error);
            return { message: 'Failed to send test notification', notification_id: '' };
        }
    }

    // Web Push Methods
    async requestNotificationPermission(): Promise<NotificationPermission> {
        if ('Notification' in window) {
            return await Notification.requestPermission();
        }
        return 'denied';
    }

    async subscribeToWebPush(): Promise<boolean> {
        try {
            if (!this.webPushSupported || !this.registration || !this.vapidPublicKey) {
                throw new Error('Web Push not supported or not properly configured');
            }

            const permission = await this.requestNotificationPermission();
            if (permission !== 'granted') {
                throw new Error('Notification permission denied');
            }

            // Get existing subscription or create new one
            let subscription = await this.registration.pushManager.getSubscription();

            if (!subscription) {
                subscription = await this.registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey),
                });
            }

            // Send subscription to backend
            await this.sendWebPushSubscription(subscription);

            console.log('Successfully subscribed to Web Push notifications');
            return true;
        } catch (error) {
            console.error('Failed to subscribe to Web Push:', error);
            return false;
        }
    }

    async unsubscribeFromWebPush(): Promise<boolean> {
        try {
            if (!this.registration) {
                return false;
            }

            const subscription = await this.registration.pushManager.getSubscription();
            if (subscription) {
                await subscription.unsubscribe();

                // Remove subscription from backend
                await api.delete('/notifications/web-push/unsubscribe/', {
                    data: { endpoint: subscription.endpoint },
                });
            }

            console.log('Successfully unsubscribed from Web Push notifications');
            return true;
        } catch (error) {
            console.error('Failed to unsubscribe from Web Push:', error);
            return false;
        }
    }

    private async sendWebPushSubscription(subscription: PushSubscription): Promise<void> {
        try {
            const subscriptionData = {
                endpoint: subscription.endpoint,
                keys: {
                    p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
                    auth: this.arrayBufferToBase64(subscription.getKey('auth')!),
                },
                user_agent: navigator.userAgent,
            };

            await api.post('/notifications/web-push/subscribe/', subscriptionData);
        } catch (error) {
            console.error('Failed to send web push subscription:', error);
        }
    }

    // FCM Methods
    async registerFCMToken(): Promise<boolean> {
        try {
            if (!this.fcmSupported) {
                return false;
            }

            const { getMessaging, getToken } = await import('firebase/messaging');
            const messaging = getMessaging();

            const token = await getToken(messaging, {
                vapidKey: this.vapidPublicKey,
            });

            if (token) {
                await api.post('/notifications/fcm/register/', {
                    token,
                    platform: 'web',
                    device_id: this.getDeviceId(),
                    app_version: import.meta.env.VITE_APP_VERSION || '1.0.0',
                });

                console.log('FCM token registered successfully');
                return true;
            }

            return false;
        } catch (error) {
            console.error('Failed to register FCM token:', error);
            return false;
        }
    }

    async unregisterFCMToken(): Promise<boolean> {
        try {
            if (!this.fcmSupported) {
                return false;
            }

            const { getMessaging, getToken } = await import('firebase/messaging');
            const messaging = getMessaging();

            const token = await getToken(messaging);
            if (token) {
                await api.delete('/notifications/fcm/unregister/', {
                    data: { token },
                });
            }

            console.log('FCM token unregistered successfully');
            return true;
        } catch (error) {
            console.error('Failed to unregister FCM token:', error);
            return false;
        }
    }

    // Utility Methods
    private urlBase64ToUint8Array(base64String: string): Uint8Array {
        const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    private arrayBufferToBase64(buffer: ArrayBuffer): string {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    private getDeviceId(): string {
        // Generate or retrieve a persistent device ID
        let deviceId = localStorage.getItem('device_id');
        if (!deviceId) {
            deviceId = 'web_' + Math.random().toString(36).substring(2, 15);
            localStorage.setItem('device_id', deviceId);
        }
        return deviceId;
    }

    // Getters
    get isWebPushSupported(): boolean {
        return this.webPushSupported;
    }

    get isFCMSupported(): boolean {
        return this.fcmSupported;
    }

    get notificationPermission(): NotificationPermission {
        return 'Notification' in window ? Notification.permission : 'denied';
    }

    // Initialize push notifications (call this after user login)
    async initializePushNotifications(): Promise<void> {
        try {
            console.log('Initializing push notifications...');

            const preferences = await this.getPreferences();

            if (preferences.web_push_enabled && this.webPushSupported) {
                await this.subscribeToWebPush();
            }

            if (preferences.fcm_enabled && this.fcmSupported) {
                await this.registerFCMToken();
            }
        } catch (error) {
            console.error('Failed to initialize push notifications:', error);
        }
    }

    // Cleanup (call this on user logout)
    async cleanup(): Promise<void> {
        try {
            if (this.webPushSupported) {
                await this.unsubscribeFromWebPush();
            }

            if (this.fcmSupported) {
                await this.unregisterFCMToken();
            }
        } catch (error) {
            console.error('Failed to cleanup notifications:', error);
        }
    }

    private checkPermission(): void {
        if ('Notification' in window) {
            this.permission = Notification.permission;
        }
    }

    async sendNotification(data: NotificationData): Promise<globalThis.Notification | null> {
        if (!this.isEnabled()) {
            console.warn('Notifications not supported or not permitted');
            return null;
        }

        const options: NotificationOptions = {
            body: data.message,
            icon: data.icon || '/favicon.ico',
            badge: '/favicon.ico',
            tag: `${data.type}-${Date.now()}`,
            data: data.data,
            requireInteraction: data.priority === 'high',
            silent: data.priority === 'low',
        };

        try {
            const notification = new globalThis.Notification(data.title, options);

            // Auto-close after 5 seconds for normal priority
            if (data.priority === 'normal') {
                setTimeout(() => {
                    notification.close();
                }, 5000);
            }

            // Handle click events
            notification.onclick = () => {
                window.focus();
                notification.close();

                // Handle specific notification types
                this.handleNotificationClick(data);
            };

            return notification;
        } catch (error) {
            console.error('Failed to send notification:', error);
            return null;
        }
    }

    private handleNotificationClick(data: NotificationData): void {
        switch (data.type) {
            case 'pronunciation_alert':
                // Navigate to speaking practice with specific phoneme
                if (data.data?.phoneme) {
                    window.location.hash = `#/student/speaking?phoneme=${data.data.phoneme}`;
                }
                break;
            case 'achievement':
                // Navigate to achievements page
                window.location.hash = '#/student/achievements';
                break;
            case 'reminder':
                // Navigate to speaking practice
                window.location.hash = '#/student/speaking';
                break;
            case 'practice_report':
                // Navigate to progress page
                window.location.hash = '#/student/progress';
                break;
        }
    }

    // Pronunciation-specific notification methods
    async sendPronunciationAlert(phoneme: string, errorCount: number): Promise<void> {
        await this.sendNotification({
            title: 'Pronunciation Alert 🗣️',
            message: `You've mispronounced /${phoneme}/ ${errorCount} times in the past 7 days. Time to practice!`,
            type: 'pronunciation_alert',
            priority: 'high',
            icon: '🗣️',
            data: { phoneme, errorCount },
        });
    }

    async sendWeeklyReport(stats: { practiceTime: number; accuracy: number; streak: number }): Promise<void> {
        await this.sendNotification({
            title: 'Weekly Practice Report 📊',
            message: `This week: ${stats.practiceTime}min practice, ${stats.accuracy}% accuracy, ${stats.streak} day streak!`,
            type: 'practice_report',
            priority: 'normal',
            icon: '📊',
            data: stats,
        });
    }

    async sendAchievementNotification(achievement: string): Promise<void> {
        await this.sendNotification({
            title: 'Achievement Unlocked! 🏆',
            message: `Congratulations! You've earned: ${achievement}`,
            type: 'achievement',
            priority: 'normal',
            icon: '🏆',
            data: { achievement },
        });
    }

    async sendPracticeReminder(): Promise<void> {
        await this.sendNotification({
            title: 'Practice Reminder 📚',
            message: "Don't forget your daily pronunciation practice!",
            type: 'reminder',
            priority: 'normal',
            icon: '📚',
        });
    }

    // Utility methods
    isEnabled(): boolean {
        return this.isSupported && this.permission === 'granted';
    }

    getPermission(): NotificationPermission {
        return this.permission;
    }

    async clearNotifications(tag?: string): Promise<void> {
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
                action: 'clearNotifications',
                tag,
            });
        }
    }

    // Schedule notifications (requires service worker)
    async scheduleNotification(
        data: NotificationData,
        delay: number,
        options: NotificationOptions = { body: data.message },
    ): Promise<void> {
        if (!this.registration) {
            console.warn('Service Worker not available for scheduled notifications');
            return;
        }

        // This would typically use the service worker's background sync
        // For now, we'll use a simple setTimeout approach
        setTimeout(() => {
            this.sendNotification(data);
        }, delay);
    }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
export type { NotificationData, NotificationOptions };
